import Codec from "./codec"
import { UserConversionJourney } from "../domain/user-conversion-journey"
import {
  AssignmentQuestionsWorkedOnCodec,
  AssignmentQuestionsWorkedOnResponse,
} from "./assignment-questions-worked-on-codec"

export interface OrganizationResponse {
  is_pro: boolean
  organization_id: string
  organization_name: string
  assignment_questions_worked_on:
    | AssignmentQuestionsWorkedOnResponse
    | undefined
  active_teachers: number
}

export interface CtaResponse {
  copy: string
  link: string
}

export interface UserConversionJourneyResponse {
  first_name: string
  last_name: string
  display_name: string
  last_active: string
  assignment_count: number
  assignment_questions_worked_on:
    | AssignmentQuestionsWorkedOnResponse
    | undefined
  user_id: string
  organization: OrganizationResponse[]
  progress_percent: number
  usage_limit: number
  show_on_ios: boolean
  blocking: boolean
  title: string
  sub_title: string
  benefits: string[]
  cta: CtaResponse
  metric: string
}

export interface UserConversionResponse {
  user_conversion: UserConversionJourneyResponse
}

export class UserConversionJourneyCodec extends Codec {
  public assignmentQuestionsWorkedOnCodec: AssignmentQuestionsWorkedOnCodec

  constructor() {
    super()

    this.assignmentQuestionsWorkedOnCodec =
      new AssignmentQuestionsWorkedOnCodec()
  }

  encode(value: UserConversionJourney): UserConversionJourneyResponse {
    return {
      first_name: value.firstName,
      last_name: value.lastName,
      display_name: value.displayName,
      last_active: value.lastActive.toISOString(),
      assignment_count: value.assignmentCount,
      assignment_questions_worked_on: value.assignmentQuestionsWorkedOn
        ? this.assignmentQuestionsWorkedOnCodec.encode(
            value.assignmentQuestionsWorkedOn,
          )
        : undefined,
      user_id: value.userId,
      organization: value.organizations?.map((org) => ({
        is_pro: org.isPro,
        organization_id: org.organizationId,
        organization_name: org.organizationName,
        assignment_questions_worked_on: org.assignmentQuestionsWorkedOn
          ? this.assignmentQuestionsWorkedOnCodec.encode(
              org.assignmentQuestionsWorkedOn,
            )
          : undefined,
        active_teachers: org.activeTeachers,
      })),
      progress_percent: value.progressPercent,
      usage_limit: value.usageLimit,
      show_on_ios: value.showOnIos,
      blocking: value.blocking,
      title: value.title,
      sub_title: value.subTitle,
      benefits: value.benefits,
      cta: {
        copy: value.cta.copy,
        link: value.cta.link,
      },
      metric: value.metric,
    }
  }

  decode(
    value: UserConversionJourneyResponse | null,
  ): UserConversionJourney | null {
    if (value) {
      return new UserConversionJourney(
        value.first_name,
        value.last_name,
        value.display_name,
        new Date(value.last_active),
        value.assignment_count,
        value.assignment_questions_worked_on
          ? this.assignmentQuestionsWorkedOnCodec.decode(
              value.assignment_questions_worked_on,
            ) || undefined
          : undefined,
        value.user_id,
        value.organization?.map((org) => ({
          isPro: org.is_pro,
          organizationId: org.organization_id,
          organizationName: org.organization_name,
          assignmentQuestionsWorkedOn: org.assignment_questions_worked_on
            ? (this.assignmentQuestionsWorkedOnCodec.decode(
                org.assignment_questions_worked_on,
              ) ?? undefined)
            : undefined,
          activeTeachers: org.active_teachers,
        })),
        value.progress_percent,
        value.usage_limit,
        value.show_on_ios,
        value.blocking,
        value.title,
        value.sub_title,
        value.benefits,
        {
          copy: value.cta?.copy,
          link: value.cta?.link,
        },
        value.metric,
      )
    }
    return null
  }
}
